import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DotsThreeIcon, ShareFatIcon, LinkSimpleIcon, ImageSquareIcon, PaperPlaneTiltIcon, DownloadSimpleIcon, ArrowClockwiseIcon, StarIcon, XIcon, MagnifyingGlassIcon, CheckIcon, CopySimpleIcon } from '@phosphor-icons/react';
import { WhatsApp, Twitter, Facebook } from '@mui/icons-material';
import {

  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  Avatar,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useUserFollow } from '../../contexts/UserFollowContext';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import { firebaseService } from '../../services/firebaseService';
import { User } from 'firebase/auth';
import CustomButton from '../widgets/CustomButton';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import CustomShareTabs from '../widgets/CustomShareTabs';
import { useUserWatchlistData } from '../../contexts/UserWatchlistDataContext';
import { getColors, glassStyles } from '../../tokens/designTokens';
import { CollaborationRequestService } from '../../services/collaborationRequestService';
import { useToast } from '../utils/ToastProvider';
import { getCachedUserProfile } from '../../utils/profileCache';
import { socialService } from '../../services/socialService';
import { watchlistService } from '../../services/watchlistService';
import { font } from '../../utils/fontHelper';
import { deepLinkService } from '../../services/deepLinkService';
import { ensureReviewShareImage, generateWatchlistShareImage, generateMovieShareImage } from '../../services/functionsApi';
import { Capacitor } from '@capacitor/core';
import { Share as CapacitorShare } from '@capacitor/share';
import { generateStoryShareImage, shareStoryImageFromUrl } from '../../services/storyShareService';
import { Logo } from '../widgets/Logo';

// Module-level session cache: avoids re-generating images when share sheet is closed and reopened.
const _imgUrlCache = new Map<string, string>();
function _cacheKey(storyId?: string, shareType?: string, itemId?: string): string {
  return storyId ?? (shareType === 'watchlist' ? `wl:${itemId ?? ''}` : `rv:${itemId ?? ''}`);
}

type ShareTab = 'users' | 'link' | 'external' | 'image';

interface ShareBottomSheetProps {
  open: boolean;
  onClose: () => void;
  shareType: 'watchlist' | 'movie' | 'profile' | 'person' | 'review';
  itemTitle: string;
  itemId?: string;
  itemUrl?: string;
  onNavigate?: (type: 'watchlist' | 'movie' | 'profile' | 'person' | 'review', id: string) => void;
  // Movie-specific props for enhanced sharing
  moviePoster?: string;
  movieYear?: string;
  movieGenre?: string;
  movieRuntime?: string;
  userRating?: number;
  userReview?: string;
  currentUser?: User;
  // Person-specific props for enhanced sharing
  personPhoto?: string;
  personDepartment?: string;
  // Review-specific props for enhanced sharing
  reviewId?: string;
  reviewMovieId?: string;
  reviewMovieName?: string;
  reviewMovieYear?: string;
  reviewMovieDirector?: string;
  reviewRating?: number;
  reviewText?: string;
  reviewAuthorName?: string;
  reviewAuthorId?: string;
  // Story-specific props — when set, adds an Image tab that generates a story share card
  storyId?: string;
  getStoryImage?: () => Promise<{ url: string; cached: boolean }>;
  initialActiveTab?: ShareTab;
  /** Override the default z-index (1500) when rendering inside high-z-index surfaces like StoryViewer (2000). */
  zIndex?: number;
  // Client-side image generation — when set, adds an Image tab rendered from a local Blob (no cloud function needed)
  getClientImage?: () => Promise<Blob>;
  /** Title shown in the sheet header when using getClientImage. */
  clientImageLabel?: string;
  /** Hide the tab bar entirely — use when only one tab is relevant (e.g. image-only share). */
  hideTabs?: boolean;
  // Collaboration-specific props
  collaborationModeOnly?: boolean;
  excludeUserIds?: string[];
  onCollaborationComplete?: () => void;
  // Permission props
  canEnableCollaboration?: boolean; // Whether user can enable collaboration (owner/admin only)
}

interface ShareUser {
  id: string;
  name: string;
  username: string;
  avatar: string;
  isFollowing: boolean;
  mutualFollowers: number;
}

interface WatchlistCoverImage {
  url: string;
  movieTitles: string[];
  movieCount: number;
  collageUrl?: string;
  posterUrls: string[];
}

interface WatchlistMetadata {
  title: string;
  description?: string;
  createdBy: string;
  createdByName: string;
  movieCount: number;
  followerCount: number;
  isPrivate: boolean;
  createdAt: string;
}

const ShareBottomSheet: React.FC<ShareBottomSheetProps> = ({
  open,
  onClose,
  shareType,
  itemTitle,
  itemId,
  itemUrl,
  onNavigate,
  moviePoster,
  movieYear,
  movieGenre,
  movieRuntime,
  userRating,
  userReview,
  currentUser,
  personPhoto,
  personDepartment,
  reviewId,
  reviewMovieId,
  reviewMovieName,
  reviewMovieYear,
  reviewMovieDirector,
  reviewRating,
  reviewText,
  reviewAuthorName,
  reviewAuthorId,
  storyId,
  getStoryImage,
  initialActiveTab,
  zIndex,
  getClientImage,
  clientImageLabel,
  hideTabs = false,
  collaborationModeOnly = false,
  excludeUserIds = [],
  onCollaborationComplete,
  canEnableCollaboration = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const colors = getColors(theme.palette.mode === 'dark');
  const { followStats } = useUserFollow();
  const { getUserDisplayName, getUserAvatar } = useUserProfileCache();
  const { getWatchlistById } = useUserWatchlistData();
  const { showSuccess, showError } = useToast();

  const [activeTab, setActiveTab] = useState<ShareTab>('users');
  const [allowCollaboration, setAllowCollaboration] = useState(false);
  const [includeRatingReview, setIncludeRatingReview] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [sharedUsers, setSharedUsers] = useState<Set<string>>(new Set());
  const [followedUsers, setFollowedUsers] = useState<ShareUser[]>([]);
  const [searchResults, setSearchResults] = useState<ShareUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [coverImage, setCoverImage] = useState<WatchlistCoverImage | null>(null);
  const [generatingCover, setGeneratingCover] = useState(false);
  const [watchlistMetadata, setWatchlistMetadata] = useState<WatchlistMetadata | null>(null);
  const [isSendingRequests, setIsSendingRequests] = useState(false);
  /** Generated review card JPEG URL (Image tab + share with files). Link-tab OG uses TMDB via /review/ + reviewShare. */
  const [reviewCardImageUrl, setReviewCardImageUrl] = useState<string | null>(null);
  const [reviewImageLoadFailed, setReviewImageLoadFailed] = useState(false);
  const [reviewImageGenerating, setReviewImageGenerating] = useState(false);

  /** Generated story/watchlist share image URL (Firebase Storage) */
  const [storyImageUrl, setStoryImageUrl] = useState<string | null>(null);
  const [_storyImageGenerating, setStoryImageGenerating] = useState(false);
  const [storyImageFailed, setStoryImageFailed] = useState(false);

  const forceRefreshPendingRef = useRef(false);
  const storyGeneratingRef = useRef(false);
  const [includeRatingInImage, setIncludeRatingInImage] = useState(false);
  const [movieImageUrl, setMovieImageUrl] = useState<string | null>(null);
  const [_movieImageGenerating, setMovieImageGenerating] = useState(false);
  const [movieImageFailed, setMovieImageFailed] = useState(false);
  const movieGeneratingRef = useRef(false);

  const [clientImageDataUrl, setClientImageDataUrl] = useState<string | null>(null);
  const [clientImageGenerating, setClientImageGenerating] = useState(false);
  const [clientImageFailed, setClientImageFailed] = useState(false);
  const clientImageGeneratingRef = useRef(false);

  // Use refs to store the latest function references to avoid dependency issues
  const getUserDisplayNameRef = useRef(getUserDisplayName);
  const getUserAvatarRef = useRef(getUserAvatar);
  const getWatchlistByIdRef = useRef(getWatchlistById);

  // Update refs when functions change
  useEffect(() => {
    getUserDisplayNameRef.current = getUserDisplayName;
    getUserAvatarRef.current = getUserAvatar;
    getWatchlistByIdRef.current = getWatchlistById;
  }, [getUserDisplayName, getUserAvatar, getWatchlistById]);

  useEffect(() => {
    if (!open) {
      setReviewCardImageUrl(null);
      setReviewImageLoadFailed(false);
      setReviewImageGenerating(false);
      setStoryImageUrl(null);
      setStoryImageFailed(false);
      setStoryImageGenerating(false);
      storyGeneratingRef.current = false;
      setMovieImageUrl(null);
      setMovieImageFailed(false);
      setMovieImageGenerating(false);
      movieGeneratingRef.current = false;
      setClientImageDataUrl(null);
      setClientImageFailed(false);
      setClientImageGenerating(false);
      clientImageGeneratingRef.current = false;
      setIncludeRatingInImage(false);
      return;
    }
    // Restore from module-level session cache so re-opening doesn't regenerate
    const key = _cacheKey(storyId, shareType, itemId);
    const cachedUrl = _imgUrlCache.get(key);
    if (cachedUrl) {
      if (shareType === 'movie') setMovieImageUrl(cachedUrl);
      else setStoryImageUrl(cachedUrl);
    }
    setActiveTab(initialActiveTab ?? 'link');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear movie image when include-rating toggle changes so the useEffect regenerates.
  const prevIncludeRatingInImageRef = useRef(includeRatingInImage);
  useEffect(() => {
    if (prevIncludeRatingInImageRef.current === includeRatingInImage) return;
    prevIncludeRatingInImageRef.current = includeRatingInImage;
    // Check session cache for the new variant first
    const movieCacheKey = _cacheKey(undefined, includeRatingInImage ? 'movie-rated' : 'movie', itemId);
    const cached = _imgUrlCache.get(movieCacheKey);
    movieGeneratingRef.current = false;
    setMovieImageFailed(false);
    if (cached) {
      setMovieImageUrl(cached);
    } else {
      setMovieImageUrl(null);
    }
  }, [includeRatingInImage, itemId]);

  /** Image tab for any signed-in user sharing a review (callable allows public reviews + your private ones). */
  const reviewThirdTabIsImage = shareType === 'review' && Boolean(currentUser?.uid) && !storyId;

  // Generate story or standalone-watchlist share image when Image tab is opened.
  useEffect(() => {
    if (!open || activeTab !== 'image') return;
    const isStoryImage = Boolean(storyId);
    const isWatchlistImage = shareType === 'watchlist' && !storyId && Boolean(itemId) && Boolean(currentUser?.uid);
    if (!isStoryImage && !isWatchlistImage) return;
    if (storyImageUrl || storyGeneratingRef.current) return;

    let cancelled = false;
    storyGeneratingRef.current = true;
    setStoryImageGenerating(true);
    void (async () => {
      try {
        const result = isStoryImage
          ? (getStoryImage ? await getStoryImage() : await generateStoryShareImage(storyId!))
          : await generateWatchlistShareImage(itemId!);
        if (!cancelled) {
          setStoryImageUrl(result.url);
          _imgUrlCache.set(_cacheKey(storyId, shareType, itemId), result.url);
        }
      } catch (e) {
        console.warn('ShareBottomSheet: image generation failed', e);
        if (!cancelled) setStoryImageFailed(true);
      } finally {
        storyGeneratingRef.current = false;
        if (!cancelled) setStoryImageGenerating(false);
      }
    })();
    return () => {
      cancelled = true;
      storyGeneratingRef.current = false;
    };
  }, [open, storyId, shareType, itemId, activeTab, storyImageUrl, getStoryImage, currentUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate movie share image when Image tab is opened.
  useEffect(() => {
    if (!open || shareType !== 'movie' || activeTab !== 'image') return;
    if (!itemId || !currentUser?.uid) return;
    if (movieImageUrl || movieGeneratingRef.current) return;

    const movieCacheKey = _cacheKey(undefined, includeRatingInImage ? 'movie-rated' : 'movie', itemId);
    let cancelled = false;
    movieGeneratingRef.current = true;
    setMovieImageGenerating(true);
    void (async () => {
      try {
        const result = await generateMovieShareImage(itemId, {
          withRating: includeRatingInImage || undefined,
          rating: includeRatingInImage && userRating ? userRating : undefined,
        });
        if (!cancelled) {
          setMovieImageUrl(result.url);
          _imgUrlCache.set(movieCacheKey, result.url);
        }
      } catch (e) {
        console.warn('ShareBottomSheet: movie image generation failed', e);
        if (!cancelled) setMovieImageFailed(true);
      } finally {
        movieGeneratingRef.current = false;
        if (!cancelled) setMovieImageGenerating(false);
      }
    })();
    return () => {
      cancelled = true;
      movieGeneratingRef.current = false;
    };
  }, [open, shareType, itemId, activeTab, movieImageUrl, currentUser?.uid, includeRatingInImage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate client-side image (Blob → data URL) when Image tab opens.
  useEffect(() => {
    if (!open || !getClientImage || activeTab !== 'image') return;
    if (clientImageDataUrl || clientImageGeneratingRef.current) return;
    let cancelled = false;
    clientImageGeneratingRef.current = true;
    setClientImageGenerating(true);
    void (async () => {
      try {
        const blob = await getClientImage();
        if (!cancelled) {
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          setClientImageDataUrl(dataUrl);
        }
      } catch (e) {
        console.warn('ShareBottomSheet: client image generation failed', e);
        if (!cancelled) setClientImageFailed(true);
      } finally {
        clientImageGeneratingRef.current = false;
        if (!cancelled) setClientImageGenerating(false);
      }
    })();
    return () => {
      cancelled = true;
      clientImageGeneratingRef.current = false;
    };
  }, [open, activeTab, getClientImage, clientImageDataUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate share card JPEG when user opens the Image tab.
  useEffect(() => {
    if (!open || shareType !== 'review' || activeTab !== 'image' || storyId) return;
    const rid = reviewId ?? itemId;
    if (!rid || !currentUser?.uid) return;
    if (forceRefreshPendingRef.current) return;
    if (reviewCardImageUrl) return;

    let cancelled = false;
    setReviewImageGenerating(true);
    void (async () => {
      try {
        const result = await ensureReviewShareImage(rid);
        if (!cancelled) setReviewCardImageUrl(result.url);
      } catch (e) {
        if (!cancelled) {
          console.warn('ShareBottomSheet: ensureReviewShareImage failed', e);
          setReviewCardImageUrl(null);
          setReviewImageLoadFailed(true);
        }
      } finally {
        if (!cancelled) setReviewImageGenerating(false);
      }
    })();
    return () => {
      cancelled = true;
      setReviewImageGenerating(false);
    };
  }, [
    open,
    shareType,
    activeTab,
    reviewId,
    itemId,
    currentUser?.uid,
    reviewCardImageUrl,
  ]);

  // Generate watchlist cover image and metadata
  const generateWatchlistCover = useCallback(async () => {
    if (shareType !== 'watchlist' || !itemId) return;

    try {
      setGeneratingCover(true);
      const watchlist = getWatchlistByIdRef.current(itemId);
      
      if (!watchlist) {
        setCoverImage(null);
        setWatchlistMetadata(null);
        return;
      }

      // Set metadata with cached user names
      setWatchlistMetadata({
        title: watchlist.name,
        description: watchlist.description,
        createdBy: watchlist.createdBy,
        createdByName: getUserDisplayNameRef.current(watchlist.createdBy, watchlist.createdByName),
        movieCount: watchlist.movieDetails.length,
        followerCount: watchlist.followerCount || 0,
        isPrivate: watchlist.isPrivate,
        createdAt: watchlist.createdAt,
      });

      if (watchlist.movieDetails.length === 0) {
        setCoverImage({
          url: '',
          movieTitles: [],
          movieCount: 0,
          posterUrls: [],
        });
        return;
      }

      // Get first movie poster only
      const firstPoster = watchlist.movieDetails.find(m => m.poster)?.poster;
      
      setCoverImage({
        url: firstPoster || '',
        movieTitles: [],
        movieCount: watchlist.movieDetails.length,
        posterUrls: firstPoster ? [firstPoster] : [],
      });
    } catch (error) {
      console.error('Error generating cover image:', error);
      setCoverImage(null);
      setWatchlistMetadata(null);
    } finally {
      setGeneratingCover(false);
    }
  }, [shareType, itemId]);

  // Load followed users when dialog opens - memoized to prevent infinite loops
  const loadFollowedUsers = useCallback(async () => {
    if (!open || !currentUser) {
      setFollowedUsers([]);
      return;
    }
    
    try {
      setLoadingUsers(true);
      
      // Get the list of users to load (either followed users or search for all users)
      const userIds = collaborationModeOnly ? [] : followStats.followedUsers;
      
      const userProfiles = await Promise.all(
        userIds.map(async (userId) => {
          try {
            const profile = await getCachedUserProfile(userId);
            if (profile) {
              return {
                id: profile.uid,
                name: getUserDisplayNameRef.current(profile.uid, profile.displayName),
                username: profile.username ? `@${profile.username}` : `@${profile.email.split('@')[0]}`,
                avatar: getUserAvatarRef.current(profile.uid) || profile.photoURL || '',
                isFollowing: true,
                mutualFollowers: 0,
              };
            }
            return null;
          } catch (error) {
            console.error(`Error fetching profile for user ${userId}:`, error);
            return null;
          }
        })
      );

      const validUsers = userProfiles.filter((user): user is ShareUser => user !== null);
      
      // Filter out excluded users for collaboration mode
      const filteredUsers = collaborationModeOnly 
        ? validUsers.filter(user => !excludeUserIds.includes(user.id))
        : validUsers;
        
      setFollowedUsers(filteredUsers);
      setSearchResults(filteredUsers);
    } catch (error) {
      console.error('Error loading followed users:', error);
      setFollowedUsers([]);
      setSearchResults([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [open, currentUser?.uid, followStats.followedUsers, collaborationModeOnly, excludeUserIds]);

  // Load data when dialog opens - using stable dependency array
  useEffect(() => {
    if (open) {
      loadFollowedUsers();
      generateWatchlistCover();
    }
  }, [open, currentUser?.uid, collaborationModeOnly, JSON.stringify(excludeUserIds), JSON.stringify(followStats.followedUsers)]);

  // Update search results when followed users change (but only if no search query)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(followedUsers);
    }
  }, [followedUsers, searchQuery]);

  // Generate share link based on item type
  const getShareLink = useCallback(() => {
    if (itemUrl) return itemUrl;

    // CRITICAL FIX: Always use production domain for sharing (fixes Capacitor localhost issue)
    // On Capacitor iOS/Android, window.location.host resolves to 'localhost' which breaks external sharing
    const baseUrl = 'https://watchlistcrew.com';
    
    switch (shareType) {
      case 'watchlist':
        const watchlistParams = new URLSearchParams();
        watchlistParams.append('shared', 'true');
        
        // Get the actual watchlist to determine the real owner
        const watchlist = itemId ? getWatchlistByIdRef.current(itemId) : null;
        
        // Add essential information for watchlist sharing
        if (currentUser) {
          watchlistParams.append('sharedBy', currentUser.uid);
        }
        
        // CRITICAL FIX: Add the actual owner ID for proper permissions
        if (watchlist?.createdBy) {
          watchlistParams.append('ownerId', watchlist.createdBy);
          console.log('🔗 ShareBottomSheet: Generated watchlist link with comprehensive data:', {
            watchlistId: itemId,
            watchlistName: watchlist.name,
            ownerId: watchlist.createdBy,
            sharedBy: currentUser?.uid,
            collaborators: watchlist.collaborators,
            collaboratorNames: watchlist.collaboratorNames,
            collaboratorProfiles: watchlist.collaboratorProfiles
          });
        }
        
        // Only add collaboration flag if needed
        if (allowCollaboration) {
          watchlistParams.append('collaborate', 'true');
        }
        return `${baseUrl}/watchlist/${itemId}?${watchlistParams.toString()}`;
      case 'movie':
        // For external sharing (social media), we now use Universal Links (watchlistcrew.com/movie/ID)
        // This is handled by Firebase Hosting rewrites which call the movieShare function for crawlers
        const movieParams = new URLSearchParams();

        // Add essential information
        if (currentUser) {
          movieParams.append("sharedBy", currentUser.uid);
        }
        if (includeRatingReview && userRating) {
          movieParams.append('rating', userRating.toString());
        }
        if (includeRatingReview && userReview) {
          movieParams.append('review', encodeURIComponent(userReview));
        }

        return deepLinkService.generateShareableLink(`/movie/${itemId}`, Object.fromEntries(movieParams));
      case 'profile':
        const profileParams = new URLSearchParams();
        profileParams.append("shared", "true");

        // Add sharer information only if different from profile being shared
        if (currentUser && currentUser.uid !== itemId) {
          profileParams.append("sharedBy", currentUser.uid);
        }

        return `${baseUrl}/profile/${itemId}?${profileParams.toString()}`;
      case 'person':
        const personParams = new URLSearchParams();
        personParams.append("shared", "true");

        // Add sharer information
        if (currentUser) {
          personParams.append("sharedBy", currentUser.uid);
        }

        return `${baseUrl}/person/${itemId}?${personParams.toString()}`;
      case 'review':
        // Hosting calls reviewShare for crawlers; browsers redirect to /open/movie/*?highlightReview=...
        return `${baseUrl}/review/${itemId}`;
      default:
        return baseUrl;
    }
  }, [itemUrl, shareType, itemId, allowCollaboration, includeRatingReview, userRating, userReview, currentUser]);



  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareLink());
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Generate share message based on share type
  const getShareMessage = useCallback(() => {
    switch (shareType) {
      case 'movie':
        return movieYear
          ? `Check out "${itemTitle}" (${movieYear}) on Watchlists`
          : `Check out "${itemTitle}" on Watchlists`;
      case 'watchlist':
        return `Check out "${itemTitle}" watchlist on Watchlists`;
      case 'profile':
        return `Check out ${itemTitle}'s profile on Watchlists`;
      case 'person':
        return `Check out ${itemTitle} on Watchlists`;
      case 'review':
        return `Check out this review of "${reviewMovieName || itemTitle}" on Watchlists`;
      default:
        return `Check this out on Watchlists!`;
    }
  }, [shareType, itemTitle, movieYear, reviewMovieName]);

  // Handle system share (native share sheet on mobile, Web Share API on desktop)
  const handleSystemShare = async () => {
    const shareLink = getShareLink();
    const shareText = getShareMessage();

    try {
      if (Capacitor.isNativePlatform()) {
        await CapacitorShare.share({
          title: itemTitle,
          text: shareText,
          url: shareLink,
          dialogTitle: `Share ${shareType}`,
        });
      } else if (navigator.share) {
        await navigator.share({
          title: itemTitle,
          text: shareText,
          url: shareLink,
        });
      } else {
        // Fallback to copy if share not available
        await handleCopyLink();
      }
    } catch (error) {
      // User cancelled share or error - silent fail
      console.log('Share cancelled or not available');
    }
  };

  const handleUserShare = (userId: string) => {
    const newSharedUsers = new Set(sharedUsers);
    if (newSharedUsers.has(userId)) {
      newSharedUsers.delete(userId);
    } else {
      newSharedUsers.add(userId);
    }
    setSharedUsers(newSharedUsers);
  };

  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults(followedUsers);
      return;
    }

    const filteredFollowed = followedUsers.filter(user =>
      user.name.toLowerCase().includes(query.toLowerCase()) ||
      user.username.toLowerCase().includes(query.toLowerCase())
    );

    try {
      setLoadingSearch(true);

      if (currentUser && query.trim().length > 2) {
        const searchedProfiles = await firebaseService.searchUsers(query.trim(), 5);
        const searchedUsers: ShareUser[] = searchedProfiles
          .filter(profile => profile.uid !== currentUser.uid)
          .map(profile => ({
            id: profile.uid,
            name: getUserDisplayNameRef.current(profile.uid, profile.displayName),
            username: profile.username ? `@${profile.username}` : `@${profile.email.split('@')[0]}`,
            avatar: getUserAvatarRef.current(profile.uid) || profile.photoURL || '',
            isFollowing: followStats.followedUsers.includes(profile.uid),
            mutualFollowers: 0,
          }));

        const combined = [...filteredFollowed];
        searchedUsers.forEach(searchUser => {
          if (!combined.find(user => user.id === searchUser.id)) {
            // Filter out excluded users for collaboration mode
            if (!collaborationModeOnly || !excludeUserIds.includes(searchUser.id)) {
              combined.push(searchUser);
            }
          }
        });

        setSearchResults(combined);
      } else {
        setSearchResults(filteredFollowed);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults(filteredFollowed);
    } finally {
      setLoadingSearch(false);
    }
  }, [followedUsers, currentUser, followStats.followedUsers, collaborationModeOnly, excludeUserIds]);

  const handleSendShares = async () => {
    if (sharedUsers.size === 0 || !currentUser || !itemId) return;

    console.log(`Sharing ${shareType} "${itemTitle}" with users:`, Array.from(sharedUsers));
    
    setIsSendingRequests(true);
    try {
      const isCollaborationMode = allowCollaboration || collaborationModeOnly;
      const userCount = sharedUsers.size;
      const userText = userCount === 1 ? 'user' : 'users';
      
      // Create notifications for each user we're sharing with
      const notificationPromises = Array.from(sharedUsers).map(async (userId) => {
        try {
          if (shareType === 'movie') {
            await firebaseService.createMovieSharedNotification(
              currentUser.uid,
              userId,
              itemId,
              itemTitle,
              'direct', // sharing method
              moviePoster,
              movieYear
            );
          } else if (shareType === 'review' && reviewMovieId) {
            await firebaseService.createMovieSharedNotification(
              currentUser.uid,
              userId,
              reviewMovieId,
              reviewMovieName || itemTitle,
              'direct',
              moviePoster,
              reviewMovieYear || movieYear
            );
          } else if (shareType === 'watchlist') {
            // Get watchlist details for additional metadata
            const watchlist = getWatchlistByIdRef.current(itemId);
            const watchlistPosters = watchlist?.movieDetails?.slice(0, 4)
              .map(m => m.poster)
              .filter((poster): poster is string => !!poster) || [];
            
            // Check if this is a collaboration invitation or regular share
            if (isCollaborationMode) {
              // Send collaboration request instead of notification
              console.log('📤 Sending collaboration request to:', userId);
              await CollaborationRequestService.sendCollaborationRequest(
                itemId,
                currentUser.uid,
                userId,
                'editor'
              );
            } else {
              await firebaseService.createWatchlistSharedNotification(
                currentUser.uid,
                userId,
                itemId,
                itemTitle,
                'direct', // sharing method
                watchlist?.movieDetails?.length,
                watchlistPosters
              );
            }
          } else if (shareType === 'profile') {
            // Share a user profile
            await firebaseService.createProfileSharedNotification(
              currentUser.uid,
              userId,
              itemId,
              itemTitle,
              'direct',
              moviePoster, // Reusing moviePoster prop for profile photo
              undefined // username could be passed if available
            );
          } else if (shareType === 'person') {
            // Share a movie person (actor/director/crew)
            await firebaseService.createPersonSharedNotification(
              currentUser.uid,
              userId,
              itemId,
              itemTitle,
              'direct',
              personPhoto,
              personDepartment
            );
          }
          console.log(`✅ Created ${shareType} ${isCollaborationMode ? 'collaboration request' : 'sharing notification'} for user ${userId}`);
        } catch (error) {
          console.error(`❌ Failed to create ${isCollaborationMode ? 'collaboration request' : 'notification'} for user ${userId}:`, error);
          throw error; // Re-throw to be caught by Promise.allSettled
        }
      });
      
      // Wait for all notifications to be created
      const results = await Promise.allSettled(notificationPromises);
      
      // Check for failures
      const failures = results.filter(result => result.status === 'rejected');
      const successes = results.filter(result => result.status === 'fulfilled');
      
      if (failures.length > 0) {
        console.error('Some notifications failed to send:', failures);
        if (successes.length > 0) {
          showError(`${isCollaborationMode ? 'Collaboration requests' : 'Notifications'} sent to ${successes.length} ${successes.length === 1 ? 'user' : 'users'}, but ${failures.length} failed.`);
        } else {
          showError(`Failed to send ${isCollaborationMode ? 'collaboration requests' : 'notifications'}. Please try again.`);
        }
      } else {
        // All succeeded
        if (isCollaborationMode) {
          showSuccess(`🤝 Collaboration requests sent to ${userCount} ${userText}! They'll receive notifications to accept or decline.`);
        } else {
          showSuccess(
            `📤 ${
              shareType === 'movie'
                ? 'Movie'
                : shareType === 'review'
                  ? 'Review'
                  : 'Watchlist'
            } shared with ${userCount} ${userText} successfully!`
          );
        }
      }
      
      console.log(`📊 Sharing results: ${successes.length} succeeded, ${failures.length} failed`);
      
    } catch (error) {
      console.error('❌ Error in sharing flow:', error);
      showError('Failed to send notifications. Please try again.');
    } finally {
      setIsSendingRequests(false);
    }
    
    // Call collaboration complete callback if in collaboration mode
    if (collaborationModeOnly && onCollaborationComplete) {
      onCollaborationComplete();
    } else {
      onClose();
    }
  };

  const handleRefreshImage = useCallback(async () => {
    const rid = reviewId ?? itemId;
    if (!rid || !currentUser?.uid || reviewImageGenerating) return;
    forceRefreshPendingRef.current = true;
    setReviewCardImageUrl(null);
    setReviewImageLoadFailed(false);
    setReviewImageGenerating(true);
    try {
      const result = await ensureReviewShareImage(rid, { force: true });
      setReviewCardImageUrl(result.url);
    } catch (e) {
      console.warn('ShareBottomSheet: force refresh failed', e);
      setReviewImageLoadFailed(true);
    } finally {
      setReviewImageGenerating(false);
      forceRefreshPendingRef.current = false;
    }
  }, [reviewId, itemId, currentUser?.uid, reviewImageGenerating]);

  const handleRefreshStoryImage = useCallback(async () => {
    if (storyGeneratingRef.current) return;
    storyGeneratingRef.current = true;
    setStoryImageUrl(null);
    setStoryImageFailed(false);
    setStoryImageGenerating(true);
    try {
      const result = storyId
        ? await generateStoryShareImage(storyId, { force: true })
        : await generateWatchlistShareImage(itemId!, { force: true });
      setStoryImageUrl(result.url);
      _imgUrlCache.set(_cacheKey(storyId, shareType, itemId), result.url);
    } catch (e) {
      console.warn('ShareBottomSheet: image refresh failed', e);
      setStoryImageFailed(true);
    } finally {
      storyGeneratingRef.current = false;
      setStoryImageGenerating(false);
    }
  }, [storyId, shareType, itemId]);

  const handleRefreshMovieImage = useCallback(async () => {
    if (movieGeneratingRef.current) return;
    movieGeneratingRef.current = true;
    setMovieImageUrl(null);
    setMovieImageFailed(false);
    setMovieImageGenerating(true);
    const movieCacheKey = _cacheKey(undefined, includeRatingInImage ? 'movie-rated' : 'movie', itemId);
    try {
      const result = await generateMovieShareImage(itemId!, {
        force: true,
        withRating: includeRatingInImage || undefined,
        rating: includeRatingInImage && userRating ? userRating : undefined,
      });
      setMovieImageUrl(result.url);
      _imgUrlCache.set(movieCacheKey, result.url);
    } catch (e) {
      console.warn('ShareBottomSheet: movie image refresh failed', e);
      setMovieImageFailed(true);
    } finally {
      movieGeneratingRef.current = false;
      setMovieImageGenerating(false);
    }
  }, [itemId, includeRatingInImage, userRating]);

  const handleSaveImage = useCallback(async () => {
    const url = movieImageUrl ?? storyImageUrl ?? reviewCardImageUrl ?? clientImageDataUrl;
    if (!url) return;
    try {
      if (Capacitor.isNativePlatform()) {
        await shareStoryImageFromUrl(url);
      } else if (url.startsWith('data:')) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `watchlists-share-${Date.now()}.jpg`;
        a.click();
      } else {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = `watchlists-share-${Date.now()}.jpg`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
      }
    } catch {
      window.open(url, '_blank');
    }
  }, [movieImageUrl, storyImageUrl, reviewCardImageUrl, clientImageDataUrl]);

  const handleInstagramShare = useCallback(async () => {
    const url = movieImageUrl ?? storyImageUrl ?? reviewCardImageUrl ?? clientImageDataUrl;
    if (!url) return;
    try {
      if (Capacitor.isNativePlatform()) {
        // Open system share sheet with image — user picks Instagram (Feed or Stories) from there.
        // Direct instagram-stories:// deep-link requires writing to the
        // com.instagram.sharedSticker.backgroundImage pasteboard key, which
        // @capacitor/clipboard cannot do. System share sheet is the reliable path.
        await shareStoryImageFromUrl(url);
      } else {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = `watchlists-share-${Date.now()}.jpg`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
      }
    } catch (e) {
      console.warn('ShareBottomSheet: Instagram share failed', e);
    }
  }, [movieImageUrl, storyImageUrl, reviewCardImageUrl, clientImageDataUrl]);

  // platformShareRow is only shown on the Image tab — all buttons here share the image.
  const handleExternalShare = async (platform: string) => {
    // Build share text + link (used on web and as caption on native)
    const link = getShareLink();
    const encodedFacebookLink = encodeURIComponent(
      shareType === 'review' && itemId
        ? `https://watchlistcrew.com/review/${itemId}`
        : link
    );
    let message = '';
    switch (shareType) {
      case 'watchlist':
        message = watchlistMetadata
          ? `🎬 ${watchlistMetadata.title}\nCreated by ${watchlistMetadata.createdByName}\n${watchlistMetadata.movieCount} movies\n\n${link}`
          : `🎬 ${itemTitle} watchlist\n\n${link}`;
        break;
      case 'movie':
        message = `🎬 ${itemTitle}${movieYear ? ` (${movieYear})` : ''}\n\n${link}`;
        break;
      case 'review': {
        let body = `🎬 ${reviewMovieName || itemTitle}`;
        if (reviewMovieYear) body += ` (${reviewMovieYear})`;
        if (reviewMovieDirector) body += `\nDirected by ${reviewMovieDirector}`;
        if (reviewRating) body += `\n⭐ ${reviewRating}/5`;
        if (reviewText) {
          const snippet = reviewText.length > 100 ? reviewText.substring(0, 100) + '...' : reviewText;
          body += `\n\n"${snippet}"`;
        }
        if (reviewAuthorName) body += `\n— ${reviewAuthorName}`;
        message = `${body}\n\n${link}`;
        break;
      }
      default:
        message = `Check this out on Watchlists!\n\n${link}`;
    }
    const encodedMessage = encodeURIComponent(message);

    const imageUrl = movieImageUrl ?? storyImageUrl ?? reviewCardImageUrl ?? clientImageDataUrl;

    if (Capacitor.isNativePlatform()) {
      // Native: system share sheet with image + text+link where available.
      // Never use URL schemes — they trigger Capacitor's navigation delegate
      // and cause unwanted SPA router navigations.
      if (imageUrl) {
        try {
          const { Filesystem, Directory } = await import('@capacitor/filesystem');
          const { Share } = await import('@capacitor/share');
          const resp = await fetch(imageUrl);
          const blob = await resp.blob();
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          const filename = `watchlists-share-${Date.now()}.jpg`;
          await Filesystem.writeFile({ path: `story_share/${filename}`, data: base64, directory: Directory.Cache, recursive: true });
          const { uri } = await Filesystem.getUri({ path: `story_share/${filename}`, directory: Directory.Cache });
          await Share.share({ title: itemTitle, text: message, url: link, files: [uri], dialogTitle: 'Share' });
        } catch (e) {
          console.warn('ShareBottomSheet: image+text share failed or cancelled', e);
        }
        // Always return after image share attempt — never show a second share sheet
        return;
      }
      // No image available yet — share text + link only
      try {
        await CapacitorShare.share({ title: itemTitle, text: message, url: link, dialogTitle: 'Share' });
      } catch (e) {
        console.warn('ShareBottomSheet: text share failed', e);
      }
      return;
    }

    // Web: platform-specific share URLs
    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodedMessage}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedFacebookLink}`, '_blank');
        break;
      default: {
        // More / unknown: Web Share API with image file if available, else text+link
        if (imageUrl) {
          try {
            const resp = await fetch(imageUrl);
            const blob = await resp.blob();
            const file = new File([blob], `watchlists-share-${Date.now()}.jpg`, { type: blob.type });
            if (navigator.canShare?.({ files: [file] })) {
              await navigator.share({ files: [file], title: itemTitle, text: message, url: link });
              return;
            }
          } catch (e) {
            const err = e as { name?: string };
            if (err?.name === 'AbortError') return;
          }
        }
        if (navigator.share) {
          navigator.share({ title: itemTitle, text: message, url: link }).catch(console.error);
        } else {
          navigator.clipboard.writeText(link).catch(console.error);
        }
      }
    }
  };

  // Person Share Card Component
  const PersonShareCard = () => {
    if (shareType !== 'person') return null;

    return (
      <Box sx={{
        border: `0px solid ${colors.ui.border}`,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        mb: 3,
      }}>
        <Box sx={{ display: 'flex', p: 2 }}>
          {personPhoto && (
            <Box sx={{
              width: 60,
              height: 90,
              borderRadius: 2,
              overflow: 'hidden',
              mr: 2,
              flexShrink: 0,
            }}>
              <Box
                component="img"
                src={personPhoto}
                alt={itemTitle}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              fontSize: 16,
              color: colors.text.primary,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {itemTitle}
            </Typography>

            {personDepartment && (
              <Typography variant="body2" sx={{
                color: colors.text.secondary,
                fontSize: 13,
                mb: 1,
              }}>
                {personDepartment}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const ShareImageLoader = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 1.5 }}>
      <Logo size="large" variant="default" state="fast-blink" />
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 14, color: colors.text.primary, lineHeight: 1.4, mb: 0.5 }}>
          Preparing the image to share
        </Typography>
        <Typography sx={{ fontFamily: font(), fontWeight: 400, fontSize: 12, color: colors.text.secondary, lineHeight: 1.4 }}>
          This usually takes around 10 seconds
        </Typography>
      </Box>
      <LinearProgress
        sx={{
          width: '70%',
          borderRadius: 4,
          height: 2,
          bgcolor: 'rgba(255,255,255,0.1)',
          '& .MuiLinearProgress-bar': { borderRadius: 4, background: 'linear-gradient(90deg, #6C64FD, #EBA9FC)' },
        }}
      />
    </Box>
  );

  // Generated review JPEG preview (Image tab)
  const ReviewShareCard = () => {
    if (shareType !== 'review' || storyId) return null;
    const rid = reviewId ?? itemId;
    if (!rid) return null;

    return (
      <Box
        sx={{
          mx: -3,
          overflow: 'hidden',
          bgcolor: 'rgba(255, 255, 255, 0.06)',
          mb: 2,
        }}
      >
        {(!reviewCardImageUrl && !reviewImageLoadFailed) && (
          <ShareImageLoader />
        )}
        {reviewCardImageUrl && (
          <>
            <Box
              component="img"
              src={reviewCardImageUrl}
              alt="Review share preview"
              sx={{
                display: 'block',
                width: '100%',
                objectFit: 'contain',
                verticalAlign: 'bottom',
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <CustomButton
                semantic="tertiary"
                variant="outlined"
                label="Regenerate image"
                icon={<ArrowClockwiseIcon size={18} />}
                onClick={() => void handleRefreshImage()}
                sx={{ fontSize: 13 }}
              />
            </Box>
          </>
        )}
        {reviewImageLoadFailed && !reviewCardImageUrl && (
          <Box sx={{ textAlign: 'center', py: 3, px: 2, justifyContent: 'center', alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
              Could not generate the share image. You can try again, or share text and link only below.
            </Typography>
            <CustomButton
              semantic="tertiary"
              variant="outlined"
              label="Try again"
              onClick={() => {
                setReviewImageLoadFailed(false);
                setReviewCardImageUrl(null);
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  // Generated story/watchlist share card preview (Image tab)
  const StoryShareCard = () => {
    const show = Boolean(storyId) || (shareType === 'watchlist' && Boolean(currentUser?.uid));
    if (!show) return null;

    return (
      <Box sx={{ mx: -3, overflow: 'hidden', bgcolor: 'rgba(255, 255, 255, 0.06)', mb: 2 }}>
        {(!storyImageUrl && !storyImageFailed) && <ShareImageLoader />}
        {storyImageUrl && (
          <>
            <Box
              component="img"
              src={storyImageUrl}
              alt="Spotlight share preview"
              sx={{
                display: 'block',
                width: '100%',
                objectFit: 'contain',
                verticalAlign: 'bottom',
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <CustomButton
                semantic="tertiary"
                variant="outlined"
                label="Regenerate image"
                icon={<ArrowClockwiseIcon size={18} />}
                onClick={() => void handleRefreshStoryImage()}
                sx={{ fontSize: 13 }}
              />
            </Box>
          </>
        )}
        {storyImageFailed && !storyImageUrl && (
          <Box sx={{ textAlign: 'center', py: 3, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
              Could not generate the share image. You can try again, or share text and link only below.
            </Typography>
            <CustomButton
              semantic="tertiary"
              variant="outlined"
              label="Try again"
              onClick={() => { setStoryImageFailed(false); setStoryImageUrl(null); }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const MovieShareImageCard = () => {
    if (shareType !== 'movie') return null;
    const hasRating = Boolean(userRating && userRating > 0);

    return (
      <Box>
        {hasRating && (
          <FormControlLabel
            control={
              <Switch
                checked={includeRatingInImage}
                onChange={(_: React.SyntheticEvent, checked: boolean) => setIncludeRatingInImage(checked)}
                size="small"
              />
            }
            label={
              <Typography sx={{ fontSize: 14, color: colors.text.secondary, fontFamily: font() }}>
                Include my rating
              </Typography>
            }
            sx={{ mb: 1.5, ml: 0 }}
          />
        )}
        <Box sx={{ mx: -3, overflow: 'hidden', bgcolor: 'rgba(255, 255, 255, 0.06)', mb: 2 }}>
          {(!movieImageUrl && !movieImageFailed) && <ShareImageLoader />}
          {movieImageUrl && (
            <>
              <Box
                component="img"
                src={movieImageUrl}
                alt="Movie share preview"
                sx={{
                  display: 'block',
                  width: '100%',
                  objectFit: 'contain',
                  verticalAlign: 'bottom',
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <CustomButton
                  semantic="tertiary"
                  variant="outlined"
                  label="Regenerate image"
                  icon={<ArrowClockwiseIcon size={18} />}
                  onClick={() => void handleRefreshMovieImage()}
                  sx={{ fontSize: 13 }}
                />
              </Box>
            </>
          )}
          {movieImageFailed && !movieImageUrl && (
            <Box sx={{ textAlign: 'center', py: 3, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
                Could not generate the share image. You can try again, or share text and link only below.
              </Typography>
              <CustomButton
                semantic="tertiary"
                variant="outlined"
                label="Try again"
                onClick={() => { setMovieImageFailed(false); setMovieImageUrl(null); }}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const ClientImageCard = () => {
    if (!getClientImage) return null;
    return (
      <Box sx={{ mx: -3, overflow: 'hidden', bgcolor: 'rgba(255, 255, 255, 0.06)', mb: 2 }}>
        {(!clientImageDataUrl && !clientImageFailed) && <ShareImageLoader />}
        {clientImageDataUrl && (
          <Box
            component="img"
            src={clientImageDataUrl}
            alt={clientImageLabel ?? 'Share preview'}
            sx={{ display: 'block', width: '100%', objectFit: 'contain', verticalAlign: 'bottom' }}
          />
        )}
        {clientImageFailed && !clientImageDataUrl && (
          <Box sx={{ textAlign: 'center', py: 3, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
              Could not generate the share image.
            </Typography>
            <CustomButton
              semantic="tertiary"
              variant="outlined"
              label="Try again"
              onClick={() => { setClientImageFailed(false); setClientImageDataUrl(null); }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const shareTabs: { label: string; value: ShareTab; icon: React.ReactNode }[] = useMemo(
    () => {
      const hasImageTab = reviewThirdTabIsImage
        || Boolean(storyId)
        || (shareType === 'watchlist' && Boolean(currentUser?.uid))
        || (shareType === 'movie' && Boolean(currentUser?.uid))
        || Boolean(getClientImage);

      const tabs: { label: string; value: ShareTab; icon: React.ReactNode }[] = [
        { label: 'Link', value: 'link' as ShareTab, icon: <LinkSimpleIcon size={20} /> },
      ];
      if (hasImageTab) {
        tabs.push({ label: 'Image', value: 'image' as ShareTab, icon: <ImageSquareIcon size={20} /> });
      }
      tabs.push({ label: 'Users', value: 'users' as ShareTab, icon: <PaperPlaneTiltIcon size={20} /> });
      return tabs;
    },
    [reviewThirdTabIsImage, storyId, shareType, currentUser?.uid]
  );

  const InstagramIcon = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  );

  const platformShareRow = (
    <Box
      sx={{
        display: 'flex',
        gap: 2.5,
        overflowX: 'auto',
        pb: 0.5,
        scrollSnapType: 'x mandatory',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {([
        { key: 'instagram' as const, icon: <InstagramIcon />, label: 'Instagram' },
        { key: 'save' as const, icon: <DownloadSimpleIcon size={24} />, label: 'Save Image' },
        { key: 'whatsapp' as const, icon: <WhatsApp />, label: 'WhatsApp' },
        { key: 'twitter' as const, icon: <Twitter />, label: 'Twitter' },
        { key: 'facebook' as const, icon: <Facebook />, label: 'Facebook' },
        { key: 'more' as const, icon: <DotsThreeIcon  weight="bold" />, label: 'More' },
      ]).map(({ key, icon, label }) => (
        <Box
          key={key}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, flexShrink: 0, scrollSnapAlign: 'start' }}
        >
          <IconButton
            onClick={
              key === 'instagram' ? () => void handleInstagramShare()
              : key === 'save' ? () => void handleSaveImage()
              : () => void handleExternalShare(key)
            }
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'rgba(255,255,255,0.08)',
              borderRadius: 3,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
            }}
          >
            {icon}
          </IconButton>
          <Typography sx={{ fontSize: 11, color: colors.text.secondary, fontFamily: font(), textAlign: 'center', lineHeight: 1.2 }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // Movie Share Card Component
  const MovieShareCard = () => {
    if (shareType !== 'movie') return null;

    return (
      <Box sx={{
        border: `0px solid ${colors.ui.border}`,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        mb: 3,
      }}>

        
        <Box sx={{ display: 'flex', p: 2 }}>
          {moviePoster && (
            <Box sx={{
              width: 40,
              height: 60,
              borderRadius: 2,
              overflow: 'hidden',
              mr: 2,
              flexShrink: 0,
            }}>
              <Box
                component="img"
                src={moviePoster}
                alt={itemTitle}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          )}
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              fontSize: 16,
              color: colors.text.primary,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {itemTitle}
            </Typography>
            
            {(movieYear || movieGenre || movieRuntime) && (
              <Typography variant="body2" sx={{
                color: colors.text.secondary,
                fontSize: 13,
                mb: 1,
              }}>
                {[movieYear, movieGenre, movieRuntime].filter(Boolean).join(' • ')}
              </Typography>
            )}
            
            {includeRatingReview && userRating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <StarIcon size={16} color="#FFD700" />
                <Typography variant="body2" sx={{
                  color: colors.text.primary,
                  fontWeight: 600,
                  fontSize: 13,
                }}>
                  {userRating}/5
                </Typography>
              </Box>
            )}
            
            {includeRatingReview && userReview && (
              <Typography variant="body2" sx={{
                color: colors.text.secondary,
                fontSize: 12,
                lineHeight: 1.4,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}>
                {userReview}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // Watchlist Cover Card Component
  const WatchlistCoverCard = () => {
    if (shareType !== 'watchlist') return null;

    return (
      <Box sx={{
        border: `0px solid ${colors.ui.border}`,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        mb: 3,
      }}>

        
        <Box sx={{ p: 2 }}>
          {generatingCover ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : coverImage && watchlistMetadata ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Single Poster */}
              <Box sx={{
                width: 120,
                height: 180,
                borderRadius: 2,
                overflow: 'hidden',
                flexShrink: 0,
              }}>
                {coverImage.url ? (
                  <Box
                    component="img"
                    src={coverImage.url}
                    alt={watchlistMetadata.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    bgcolor: colors.background.secondary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Typography sx={{ color: colors.text.tertiary, fontSize: '2rem' }}>
                      📋
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {/* Metadata */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  fontSize: 16,
                  color: colors.text.primary,
                  mb: 0.5,
                }}>
                  {watchlistMetadata.title}
                </Typography>
                
                <Typography variant="body2" sx={{
                  color: colors.text.secondary,
                  fontSize: 12,
                  mb: 1,
                }}>
                  Created by {watchlistMetadata.createdByName}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="body2" sx={{
                    color: colors.text.secondary,
                    fontSize: 12,
                  }}>
                    {watchlistMetadata.movieCount} movies
                  </Typography>
                  
                  {watchlistMetadata.followerCount > 0 && (
                    <Typography variant="body2" sx={{
                      color: colors.text.secondary,
                      fontSize: 12,
                    }}>
                      {watchlistMetadata.followerCount} {watchlistMetadata.followerCount === 1 ? 'like' : 'likes'}
                    </Typography>
                  )}
                  
                  {watchlistMetadata.isPrivate && (
                    <Box sx={{
                      bgcolor: colors.ui.border,
                      color: colors.text.secondary,
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: 10,
                      fontWeight: 500,
                    }}>
                      Private
                    </Box>
                  )}
                </Box>
                
                {watchlistMetadata.description && (
                  <Typography variant="body2" sx={{
                    color: colors.text.secondary,
                    fontSize: 12,
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}>
                    {watchlistMetadata.description}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : watchlistMetadata && !coverImage ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                fontSize: 16,
                color: colors.text.primary,
                mb: 1,
              }}>
                {watchlistMetadata.title}
              </Typography>
              
              {watchlistMetadata.description && (
                <Typography variant="body2" sx={{
                  color: colors.text.secondary,
                  fontSize: 12,
                  mb: 1,
                }}>
                  {watchlistMetadata.description}
                </Typography>
              )}
              
              <Typography variant="body2" sx={{
                color: colors.text.secondary,
                fontSize: 12,
              }}>
                Created by {watchlistMetadata.createdByName} • {watchlistMetadata.movieCount} movies
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{
              color: colors.text.secondary,
              textAlign: 'center',
              py: 2,
            }}>
              No movies in watchlist
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  if (isMobile) {
    // Mobile Bottom Sheet
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        height="80dvh"
        zIndex={zIndex ?? 1500}
      >
      {/* Header */}
      <BottomSheetDraggableHeader sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 3,
        pb: 0,
      }}>
        <Typography variant="h6" sx={{
          fontFamily: font(),
          fontWeight: 600,
          fontSize: 18, // 18px from Figma
          lineHeight: 1.555, // ~28px line height for 18px font from Figma
          color: '#FFFFFF', // White text from Figma
        }}>
          {collaborationModeOnly
            ? 'Invite Collaborators'
            : clientImageLabel
              ? clientImageLabel
              : shareType === 'movie'
                ? 'Share Movie'
                : shareType === 'profile'
                  ? 'Share Profile'
                  : shareType === 'person'
                    ? 'Share Person'
                    : shareType === 'review'
                      ? 'Share Review'
                      : 'Share Watchlist'
          }
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{
            color: colors.text.secondary,
            '&:hover': { bgcolor: colors.ui.hover },
          }}
        >
          <XIcon size={20} />
        </IconButton>
      </BottomSheetDraggableHeader>

      <Box sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Custom Tabs */}
        {!hideTabs && (
        <Box sx={{
          p: 3,
          pb: 1.5,
          pt: 1.5,
        }}>
          <CustomShareTabs
            value={activeTab}
            onChange={(newValue) => setActiveTab(newValue as ShareTab)}
            tabs={shareTabs}
          />
        </Box>
        )}

        <Box sx={{ p: 3, flex: 1, overflow: 'auto', minHeight: 0, minWidth: 0 }}>
          {/* Users Tab */}
          {activeTab === 'users' && (
            <Box>
              {!collaborationModeOnly && canEnableCollaboration && (
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowCollaboration}
                        onChange={(e) => setAllowCollaboration(e.target.checked)}
                      />
                    }
                    label="Allow collaboration on this watchlist"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: 14,
                        fontFamily: font(),
                        color: colors.text.primary,
                      },
                    }}
                  />
                </Box>
              )}
              {/* Search */}
              <TextField
                fullWidth
                placeholder="Search users"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MagnifyingGlassIcon size={20} color={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'} />
                    </InputAdornment>
                  ),
                  endAdornment: loadingSearch && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '12px',
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
                    '&.Mui-focused fieldset': { 
                      borderColor: theme.palette.primary.main,
                      borderWidth: '2px'
                    },
                    '& input': {
                      fontFamily: font(),
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                      '&::placeholder': {
                        color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                        opacity: 1,
                      }
                    }
                  },
                }}
              />

              {/* User List */}
              {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1 
                  }}>
                  {searchResults.map((user) => (
                    <Box
                      key={user.id}
                      onClick={() => handleUserShare(user.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5, // 12px gap from Figma
                        p: 2,
                        borderRadius: '12px', // 8px border radius from Figma
                        cursor: 'pointer',
                        bgcolor: sharedUsers.has(user.id) ? 'rgba(82, 61, 215, 0.2)' : 'rgba(255, 255, 255, 0.05)', // Colors from Figma
                        border: sharedUsers.has(user.id) ? '1px solid #7D7AFF' : '1px solid transparent',
                        '&:hover': {
                          bgcolor: sharedUsers.has(user.id) ? 'rgba(125, 122, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <Avatar
                        src={user.avatar}
                        sx={{ width: 40, height: 40 }}
                      />
                      
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{
                          fontFamily: font(),
                          fontWeight: 400,
                          fontSize: 14,
                          lineHeight: 1.428,
                          color: 'rgba(255, 255, 255, 0.9)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {user.name}
                        </Typography>
                        <Typography variant="body2" sx={{
                          fontFamily: font(),
                          fontWeight: 400,
                          fontSize: 12,
                          lineHeight: 1.333,
                          color: 'rgba(255, 255, 255, 0.6)',
                        }}>
                          {user.username}
                        </Typography>
                      </Box>
                      
                    </Box>
                  ))}
                  
                  {searchResults.length === 0 && !loadingUsers && (
                    <Typography variant="body2" sx={{
                      color: colors.text.secondary,
                      textAlign: 'center',
                      py: 4,
                    }}>
                      {searchQuery ? 'No users found' : followStats.followedUsers.length === 0 ? 'Search for users to share with' : 'No followed users'}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Link Tab */}
          {activeTab === 'link' && (
            <Box>
              {/* Share Card Preview */}
              <PersonShareCard />
              <MovieShareCard />
              <WatchlistCoverCard />

              {/* Link Display */}
              <Box sx={{
                border: `1px solid ${colors.ui.border}`,
                borderRadius: 2,
                p: 2,
                bgcolor: colors.background.secondary,
                mb: 2,
              }}>
                <Typography variant="body2" sx={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: colors.text.secondary,
                  wordBreak: 'break-all',
                  lineHeight: 1.5,
                }}>
                  {getShareLink()}
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <CustomButton
                  onClick={handleSystemShare}
                  semantic="tertiary"
                  variant="contained"
                  icon={<ShareFatIcon />}
                  label="Share"
                  sx={{ flex: 1, py: 1.5 }}
                />
                <CustomButton
                  onClick={handleCopyLink}
                  semantic={linkCopied ? "success" : "brand"}
                  icon={linkCopied ? <CheckIcon size={18} /> : <CopySimpleIcon size={18} />}
                  label={linkCopied ? 'Copied!' : 'Copy Link'}
                  sx={{ flex: 1, py: 1.5 }}
                />
              </Box>
            </Box>
          )}

          {/* Image tab: client-side, movie, story spotlight, or review card */}
          {activeTab === 'image' && (
            <Box>
              <ClientImageCard />
              <MovieShareImageCard />
              <StoryShareCard />
              <ReviewShareCard />
            </Box>
          )}
        </Box>

        {/* Fixed share row for image tab */}
        {activeTab === 'image' && (
          <Box sx={{ flexShrink: 0, pt: 1.5, pb: 2, px: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            {platformShareRow}
          </Box>
        )}

        {/* Bottom Action Bar - Only shown when Users tab is active */}
        {activeTab === 'users' && sharedUsers.size > 0 && (
          <Box sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            mt: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          }}>
            <CustomButton
              onClick={handleSendShares}
              variant="contained"
              semantic="brand"
              icon={isSendingRequests ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PaperPlaneTiltIcon size={18} />}
              label={isSendingRequests ? 'Sending...' : `Send to ${sharedUsers.size} ${sharedUsers.size === 1 ? 'user' : 'users'}`}
              disabled={isSendingRequests}
              sx={{
                py: 1.5,
                px: 3,
                width: '100%',
                borderRadius: 2, // 12px border radius from Figma
                fontFamily: font(),
                fontWeight: 500,
                fontSize: 14,
                textTransform: 'none',
              }}
            />
          </Box>
        )}
      </Box>
      </BottomSheet>
    );
  }

  // Desktop Dialog
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      style={zIndex != null ? { zIndex } : undefined}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          ...glassStyles.popup(theme.palette.mode === 'dark'),
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        fontFamily: font(),
        fontWeight: 600,
        fontSize: 18,
        lineHeight: 1.555,
        color: '#FFFFFF',
        pb: 0,
      }}>
        {collaborationModeOnly
          ? 'Invite Collaborators'
          : clientImageLabel
            ? clientImageLabel
            : shareType === 'movie'
              ? 'Share Movie'
              : shareType === 'profile'
                ? 'Share Profile'
                : shareType === 'person'
                  ? 'Share Person'
                  : shareType === 'review'
                    ? 'Share Review'
                    : 'Share Watchlist'
        }
      </DialogTitle>

      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: colors.text.secondary,
          '&:hover': { bgcolor: colors.ui.hover },
          zIndex: 1,
        }}
      >
        <XIcon size={20} />
      </IconButton>

      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Custom Tabs */}
        {!hideTabs && (
        <Box sx={{
          p: 3,
          pb: 1.5,
          pt: 1.5,
        }}>
          <CustomShareTabs
            value={activeTab}
            onChange={(newValue) => setActiveTab(newValue as ShareTab)}
            tabs={shareTabs}
          />
        </Box>
        )}

        <Box sx={{ p: 3, flex: 1, overflow: 'auto', minHeight: 0, minWidth: 0 }}>
          {/* Users Tab */}
          {activeTab === 'users' && (
            <Box>
              {!collaborationModeOnly && canEnableCollaboration && (
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowCollaboration}
                        onChange={(e) => setAllowCollaboration(e.target.checked)}
                      />
                    }
                    label="Allow collaboration on this watchlist"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: 14,
                        fontFamily: font(),
                        color: colors.text.primary,
                      },
                    }}
                  />
                </Box>
              )}
              {/* Search */}
              <TextField
                fullWidth
                placeholder="Search users"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MagnifyingGlassIcon size={20} color={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'} />
                    </InputAdornment>
                  ),
                  endAdornment: loadingSearch && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '12px',
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
                    '&.Mui-focused fieldset': { 
                      borderColor: theme.palette.primary.main,
                      borderWidth: '2px'
                    },
                    '& input': {
                      fontFamily: font(),
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                      '&::placeholder': {
                        color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                        opacity: 1,
                      }
                    }
                  },
                }}
              />

              {/* User List */}
              {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                  }}>
                  {searchResults.map((user) => (
                    <Box
                      key={user.id}
                      onClick={() => handleUserShare(user.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 2,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        bgcolor: sharedUsers.has(user.id) ? 'rgba(82, 61, 215, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                        border: sharedUsers.has(user.id) ? '1px solid #7D7AFF' : '1px solid transparent',
                        '&:hover': {
                          bgcolor: sharedUsers.has(user.id) ? 'rgba(125, 122, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <Avatar
                        src={user.avatar}
                        sx={{ width: 40, height: 40 }}
                      />

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{
                          fontFamily: font(),
                          fontWeight: 400,
                          fontSize: 14,
                          lineHeight: 1.428,
                          color: 'rgba(255, 255, 255, 0.9)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {user.name}
                        </Typography>
                        <Typography variant="body2" sx={{
                          fontFamily: font(),
                          fontWeight: 400,
                          fontSize: 12,
                          lineHeight: 1.333,
                          color: 'rgba(255, 255, 255, 0.6)',
                        }}>
                          {user.username}
                        </Typography>
                      </Box>

                    </Box>
                  ))}

                  {searchResults.length === 0 && !loadingUsers && (
                    <Typography variant="body2" sx={{
                      color: colors.text.secondary,
                      textAlign: 'center',
                      py: 4,
                    }}>
                      {searchQuery ? 'No users found' : followStats.followedUsers.length === 0 ? 'Search for users to share with' : 'No followed users'}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Link Tab */}
          {activeTab === 'link' && (
            <Box>
              {/* Share Card Preview */}
              <PersonShareCard />
              <MovieShareCard />
              <WatchlistCoverCard />

              {/* Link Display */}
              <Box sx={{
                border: `1px solid ${colors.ui.border}`,
                borderRadius: 2,
                p: 2,
                bgcolor: colors.background.secondary,
                mb: 2,
              }}>
                <Typography variant="body2" sx={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: colors.text.secondary,
                  wordBreak: 'break-all',
                  lineHeight: 1.5,
                }}>
                  {getShareLink()}
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <CustomButton
                  onClick={handleSystemShare}
                  semantic="tertiary"
                  variant="contained"
                  icon={<ShareFatIcon />}
                  label="Share"
                  sx={{ flex: 1, py: 1.5 }}
                />
                <CustomButton
                  onClick={handleCopyLink}
                  semantic={linkCopied ? "success" : "brand"}
                  icon={linkCopied ? <CheckIcon size={18} /> : <CopySimpleIcon size={18} />}
                  label={linkCopied ? 'Copied!' : 'Copy Link'}
                  sx={{ flex: 1, py: 1.5 }}
                />
              </Box>
            </Box>
          )}

          {activeTab === 'image' && (
            <Box>
              <ClientImageCard />
              <MovieShareImageCard />
              <StoryShareCard />
              <ReviewShareCard />
            </Box>
          )}
        </Box>

        {/* Fixed share row for image tab */}
        {activeTab === 'image' && (
          <Box sx={{ flexShrink: 0, pt: 1.5, pb: 2, px: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            {platformShareRow}
          </Box>
        )}

        {/* Bottom Action Bar - Only shown when Users tab is active */}
        {activeTab === 'users' && sharedUsers.size > 0 && (
          <Box sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          }}>
            <CustomButton
              onClick={handleSendShares}
              variant="contained"
              semantic="brand"
              icon={isSendingRequests ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PaperPlaneTiltIcon size={18} />}
              label={isSendingRequests ? 'Sending...' : `Send to ${sharedUsers.size} ${sharedUsers.size === 1 ? 'user' : 'users'}`}
              disabled={isSendingRequests}
              sx={{
                py: 1.5,
                px: 3,
                width: '100%',
                borderRadius: 2,
                fontFamily: font(),
                fontWeight: 500,
                fontSize: 14,
                textTransform: 'none',
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareBottomSheet; 
